import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, tap } from 'rxjs/operators';

import { ComponentTypeEnum } from '~/attendee/enums/component.enum';
import { AttendeeMenuCms } from '~/cms/interfaces/attendee-menu.cms.interface';
import { CheckoutFooterCms } from '~/cms/interfaces/checkout-footer.cms.interface';
import { ConfigurationCms } from '~/cms/interfaces/configuration.interface';
import { DynamicPageCms } from '~/cms/interfaces/dynamic-page.cms.interface';
import {
  CmsPage,
  CmsSectionType,
  HomepageCms,
  OfferPageCms,
  OfferSectionCms,
} from '~/cms/interfaces/homepage.cms.interface';
import { FooterCms } from '~/cms/interfaces/footer.cms.interface';
import { SitemapCms } from '~/cms/interfaces/sitemap.cms.interface';
import { SportBannerCms } from '~/cms/interfaces/sport-banner.cms.interface';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '~environments/environment';

export const CMS_BASE_URL = new InjectionToken<string>('Strapi BASE_URL');

@Injectable({
  providedIn: 'root',
})
export class CmsService {
  private readonly cmsLocale = environment?.applications?.strapi?.locale;
  private readonly configurationSubject: BehaviorSubject<ConfigurationCms | null> =
    new BehaviorSubject<ConfigurationCms | null>(null);
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  public cache: Record<string, any> = {};
  public isRequestInProgress: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Inject(CMS_BASE_URL) private readonly cmsUrl: string,
    private readonly httpClient: HttpClient,
    private readonly translateService: TranslateService
  ) {}

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private getWithCache<T>(path: string, localized = true): Observable<T> {
    const locales = Object.keys(
      environment?.applications?.phrase?.locales || {}
    );

    let completePath = path;

    if (localized && locales.length > 1) {
      const locale = this.translateService.currentLang;
      const separator = path.includes('?') ? '&' : '?';
      // check if locale is different in Strapi conf
      const localeToSend =
        this.cmsLocale &&
        this.cmsLocale !== '' &&
        locale.includes(this.cmsLocale)
          ? this.cmsLocale
          : locale;

      completePath = `${path}${separator}_locale=${localeToSend}`;
    }

    if (this.cache[completePath]) {
      return of(this.cache[completePath]);
    }

    return this.httpClient.get<T>(`${this.cmsUrl}${completePath}`).pipe(
      tap((data) => (this.cache[completePath] = data)),
      shareReplay(1)
    );
  }

  private isOfferPageSection(
    section: CmsSectionType
  ): section is OfferSectionCms {
    return section.__component === ComponentTypeEnum.OFFER;
  }

  private findOffers(offerPages, offerSections): void {
    return offerPages.forEach((offerPage) => {
      offerSections.find(
        (section) => section?.offer_page?.slug === offerPage?.slug
      ).offer_page = offerPage;
    });
  }

  public getAttendeeMenu(): Observable<AttendeeMenuCms> {
    const cacheKey = 'cms-menu';
    const cacheTimeKey = 'cms_timestamp';
    const oneHour = 60 * 60 * 1000;
    const now = Date.now();

    if (this.isBrowser) {
      const cachedData = localStorage.getItem(cacheKey);
      const cachedTime = localStorage.getItem(cacheTimeKey);
      if (
        cachedData &&
        cachedTime &&
        now - parseInt(cachedTime, 10) < oneHour
      ) {
        const parsedData = JSON.parse(cachedData);
        return of(parsedData);
      }
    }

    return this.getWithCache<AttendeeMenuCms>('/attendee-menu').pipe(
      tap((data) => {
        if (this.isBrowser) {
          localStorage.setItem(cacheKey, JSON.stringify(data));
          localStorage.setItem(cacheTimeKey, now.toString());
        }
        return data;
      })
    );
  }

  public getCheckoutFooter(): Observable<CheckoutFooterCms> {
    return this.getWithCache<CheckoutFooterCms>('/checkout-footer');
  }

  public getConfiguration(): Observable<ConfigurationCms> {
    const cacheKey = 'cms-conf';
    const cacheTimeKey = 'cms_timestamp';
    const oneHour = 60 * 60 * 1000;
    const now = Date.now();

    if (this.configurationSubject.value) {
      return this.configurationSubject.asObservable();
    }

    if (this.isRequestInProgress) {
      return this.configurationSubject.asObservable();
    }

    if (this.isBrowser) {
      const cachedData = localStorage.getItem(cacheKey);
      const cachedTime = localStorage.getItem(cacheTimeKey);
      if (
        cachedData &&
        cachedTime &&
        now - parseInt(cachedTime, 10) < oneHour
      ) {
        const parsedData = JSON.parse(cachedData);
        this.configurationSubject.next(parsedData);
        return this.configurationSubject.asObservable();
      }
    }

    this.isRequestInProgress = true;

    return this.httpClient
      .get<ConfigurationCms>(`${this.cmsUrl}/configuration`)
      .pipe(
        tap((data) => {
          this.configurationSubject.next(data);
          if (this.isBrowser) {
            localStorage.setItem(cacheKey, JSON.stringify(data));
            localStorage.setItem(cacheTimeKey, now.toString());
          }
          this.isRequestInProgress = false;
        }),
        shareReplay(1),
        catchError((error) => {
          this.configurationSubject.error(error);
          this.isRequestInProgress = false;
          throw new Error('Strapi configuration error');
        })
      );
  }

  public getDynamicPageBySlug(slug: string): Observable<DynamicPageCms> {
    return this.getWithCache<DynamicPageCms[]>(
      `/dynamic-pages?slug=${slug}`
    ).pipe(
      map((pages) => pages[0]),
      mergeMap((dynamicPage) => {
        if (!dynamicPage) {
          return of(null);
        }

        const offerSections = dynamicPage?.sections.filter(
          this.isOfferPageSection
        );

        // If there is no offer section within the dynamic page, nothing more to do.
        if (offerSections?.length === 0) {
          return of(dynamicPage);
        }

        // Due to a Strapi bug not returning the deep attributes of an offer page included in
        // a dynamic page (e.g. price range), we have to re-call Strapi for each offer page
        // and replace the offer sections of the dynamic page with the results.
        return forkJoin(
          offerSections.map((section) =>
            this.getOfferPageBySlug(section?.offer_page?.slug)
          )
        ).pipe(
          map((offerPages) => {
            this.findOffers(offerPages, offerSections);

            return dynamicPage;
          })
        );
      })
    );
  }

  public getFooter(): Observable<FooterCms> {
    const cacheKey = 'cms-footer';
    const cacheTimeKey = 'cms_timestamp';
    const oneHour = 60 * 60 * 1000;
    const now = Date.now();

    if (this.isBrowser) {
      const cachedData = localStorage.getItem(cacheKey);
      const cachedTime = localStorage.getItem(cacheTimeKey);
      if (
        cachedData &&
        cachedTime &&
        now - parseInt(cachedTime, 10) < oneHour
      ) {
        const parsedData = JSON.parse(cachedData);
        return of(parsedData);
      }
    }

    return this.getWithCache<FooterCms>('/footer').pipe(
      tap((data) => {
        if (this.isBrowser) {
          localStorage.setItem(cacheKey, JSON.stringify(data));
          localStorage.setItem(cacheTimeKey, now.toString());
        }
      })
    );
  }

  public getHomepage(): Observable<HomepageCms> {
    return this.getWithCache<HomepageCms>('/homepage-v2');
  }

  public getLogo(logoName: string): Observable<string> {
    return this.getConfiguration().pipe(
      map((configuration) => {
        return configuration?.design?.[logoName]?.url;
      })
    );
  }

  public getOfferPageBySlug(slug: string): Observable<OfferPageCms> {
    return this.getWithCache<OfferPageCms[]>(`/offer-pages?slug=${slug}`).pipe(
      map((pages) => pages[0])
    );
  }

  public getOrganizerFooter(): Observable<FooterCms> {
    return this.getWithCache<FooterCms>('/footer-organizer');
  }

  public getPageBySlug(slug: string): Observable<CmsPage> {
    return this.getWithCache<CmsPage[]>(`/pages?slug=${slug}`).pipe(
      map((pages) => pages[0])
    );
  }

  public getSitemap(id: string): Observable<SitemapCms> {
    return this.getWithCache<SitemapCms>('/sitemap', false).pipe(
      map((sitemap) => sitemap)
    );
  }

  public getSportBanner(sportGroup: number): Observable<SportBannerCms> {
    return this.getWithCache<SportBannerCms[]>(
      `/sport-banners?sport_group=${sportGroup}`
    ).pipe(map((banners) => banners[0]));
  }
}
